import { useEffect, useMemo, useRef, useState } from "react";
import { api, assetUrl } from "@/lib/api";
import { X, CaretLeft, CaretRight, Play, Pause, MapPin, SpinnerGap, Sparkle } from "@phosphor-icons/react";

const SLIDE_MS = 4000;

// Full-screen swipeable "Story" reel of an album's memories (FB/IG style, auto-advancing).
export default function AlbumMemories({ albumId, open, onOpenChange }) {
  const [data, setData] = useState(undefined);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const touchX = useRef(null);

  // Flatten chapters → slides.
  const slides = useMemo(() => {
    if (!data?.chapters) return [];
    const out = [];
    data.chapters.forEach((ch) => {
      ch.photos.forEach((p, i) => out.push({ photo: p, chapter: ch, isChapterStart: i === 0 }));
    });
    return out;
  }, [data]);

  useEffect(() => {
    if (!open) return;
    setData(undefined); setIdx(0); setPaused(false);
    api.get(`/shared-albums/${albumId}/memories`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [open, albumId]);

  const close = () => onOpenChange(false);
  const next = () => setIdx((i) => i + 1);
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  // End of reel → close (kept out of the setIdx updater to avoid setState-in-render).
  useEffect(() => {
    if (open && slides.length > 0 && idx >= slides.length) close();
  }, [idx, slides.length, open]);

  useEffect(() => {
    if (!open || paused || slides.length === 0) return;
    timer.current = setTimeout(next, SLIDE_MS);
    return () => clearTimeout(timer.current);
  }, [idx, paused, open, slides.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "ArrowRight") next(); else if (e.key === "ArrowLeft") prev(); else if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, slides.length]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black" data-testid="album-memories">
      {data === undefined && <div className="grid h-full place-items-center"><SpinnerGap size={34} className="animate-spin text-white/80" /></div>}
      {data === null && (
        <div className="grid h-full place-items-center text-center text-white/80">
          <div><p>Could not load memories.</p><button onClick={close} className="mt-3 rounded-full bg-white/15 px-4 py-2 text-sm">Close</button></div>
        </div>
      )}
      {data && slides.length === 0 && (
        <div className="grid h-full place-items-center px-8 text-center text-white/80">
          <div>
            <Sparkle size={40} weight="fill" className="mx-auto text-[#F27A54]" />
            <p className="mt-3 font-display text-lg font-bold text-white">No memories yet</p>
            <p className="mt-1 text-sm">Add dated photos to this album and they'll turn into memory reels.</p>
            <button data-testid="memories-close" onClick={close} className="mt-4 rounded-full bg-white/15 px-5 py-2 text-sm font-semibold">Close</button>
          </div>
        </div>
      )}

      {data && slides.length > 0 && idx < slides.length && (() => {
        const s = slides[idx];
        const p = s.photo;
        const date = p.date_taken ? new Date(p.date_taken).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : null;
        const place = [p.location?.city, p.location?.country].filter(Boolean).join(", ");
        return (
          <div className="relative h-full w-full select-none"
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; setPaused(true); }}
            onTouchEnd={(e) => {
              setPaused(false);
              const dx = (e.changedTouches[0].clientX - (touchX.current ?? 0));
              if (dx < -40) next(); else if (dx > 40) prev();
            }}>
            {/* Progress bars */}
            <div className="absolute left-0 right-0 top-0 z-20 flex gap-1 p-3">
              {slides.map((_, i) => (
                <div key={i} className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30">
                  <div className="h-full bg-white" style={
                    i < idx ? { width: "100%" }
                      : i === idx ? { width: "0%", animation: paused ? "none" : `storyfill ${SLIDE_MS}ms linear forwards` }
                      : { width: "0%" }
                  } />
                </div>
              ))}
            </div>

            {/* Image */}
            <img key={p.id} src={assetUrl(p.image_url)} alt={p.title || ""} className="h-full w-full object-contain" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />

            {/* Chapter label */}
            <div className="absolute left-4 top-8 z-10">
              <p className="font-display text-sm font-black text-white drop-shadow">{s.chapter.title}</p>
              <p className="text-[11px] font-semibold text-white/75">{s.chapter.subtitle}</p>
            </div>

            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 z-10 p-6">
              {p.title && <h3 className="font-display text-2xl font-black text-white drop-shadow">{p.title}</h3>}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-white/85">
                {place && <span className="flex items-center gap-1"><MapPin size={13} weight="fill" /> {place}</span>}
                {date && <span>{date}</span>}
              </div>
            </div>

            {/* Controls */}
            <button data-testid="memories-close" onClick={close} className="absolute right-3 top-6 z-30 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white hover:bg-black/60"><X size={18} weight="bold" /></button>
            <button data-testid="memories-pause" onClick={() => setPaused((v) => !v)} className="absolute right-14 top-6 z-30 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white hover:bg-black/60">{paused ? <Play size={16} weight="fill" /> : <Pause size={16} weight="fill" />}</button>

            {/* Tap zones */}
            <button data-testid="memories-prev" aria-label="Previous" onClick={prev} className="absolute left-0 top-0 z-10 h-full w-1/3" />
            <button data-testid="memories-next" aria-label="Next" onClick={next} className="absolute right-0 top-0 z-10 h-full w-2/3" />
            <div className="pointer-events-none absolute left-3 top-1/2 hidden -translate-y-1/2 text-white/40 sm:block"><CaretLeft size={26} /></div>
            <div className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 text-white/40 sm:block"><CaretRight size={26} /></div>
          </div>
        );
      })()}
    </div>
  );
}
